import type { SeriesChartSeries } from '../../components/charts/SeriesChart';

/**
 * E34 · WorksheetEntriesOverTime — the PURE part of the hub's "Over time" page: the read's field list, the
 * row type, the chart's series and the table's rows. No i18n here (helpers return keys or data), no verdict:
 * the numbers as they were answered.
 */
export const OVER_TIME_FIELDS = [
  'subject',
  'templateId',
  'name',
  'entryCount',
  { numericFields: ['fieldKey', 'label', 'type', 'min', 'max'] },
  { points: ['submissionId', 'occurredAt', 'fieldKey', 'value'] },
  {
    series: [
      'fieldKey',
      'label',
      'type',
      'min',
      'max',
      'latest',
      'count',
      { points: ['submissionId', 'occurredAt', 'value'] },
    ],
  },
];

export type OverTimeSeriesData = {
  fieldKey: string;
  label: string;
  /** number · scale · score */
  type: string;
  min: number | null;
  max: number | null;
  latest: number | null;
  count: number;
  points: Array<{ submissionId: string; occurredAt: string; value: number }>;
};

export type OverTimeData = {
  subject: string;
  templateId: string;
  name: string | null;
  entryCount: number;
  numericFields: Array<{ fieldKey: string; label: string; type: string }>;
  points: Array<{
    submissionId: string;
    occurredAt: string;
    fieldKey: string;
    value: number;
  }>;
  series: OverTimeSeriesData[];
};

export const SCORE_KEY = 'score';

/** The label a series is drawn and listed with — the score's is the app's word, not the API's. */
export const seriesLabel = (
  s: Pick<OverTimeSeriesData, 'fieldKey' | 'label'>,
  scoreWord: string,
): string => (s.fieldKey === SCORE_KEY ? scoreWord : s.label);

export const chartSeriesOf = (
  data: Pick<OverTimeData, 'series'>,
  scoreWord: string,
): SeriesChartSeries[] =>
  data.series.map((s) => ({
    key: s.fieldKey,
    label: seriesLabel(s, scoreWord),
    points: s.points.map((p) => ({ x: p.occurredAt, y: p.value })),
  }));

/**
 * One y range for the chart when every drawn line is a field on a declared range (a 0–10 scale): the widest
 * min / max among them. The score is a sum on its own scale, so it is never forced into the fields' range.
 */
export const yRangeOf = (
  data: Pick<OverTimeData, 'series'>,
): [number, number] | undefined => {
  const fields = data.series.filter((s) => s.fieldKey !== SCORE_KEY);
  if (!fields.length || data.series.length !== fields.length) return undefined;
  if (fields.some((s) => s.min === null || s.max === null)) return undefined;
  return [
    Math.min(...fields.map((s) => s.min as number)),
    Math.max(...fields.map((s) => s.max as number)),
  ];
};

export type TableRow = {
  submissionId: string;
  occurredAt: string;
  values: Record<string, number | undefined>;
};

/** One row per submission, newest first; a field it did not answer is simply absent. */
export const tableRowsOf = (data: Pick<OverTimeData, 'points'>): TableRow[] => {
  const rows = new Map<string, TableRow>();
  for (const p of data.points) {
    const row = rows.get(p.submissionId) ?? {
      submissionId: p.submissionId,
      occurredAt: p.occurredAt,
      values: {},
    };
    row.values[p.fieldKey] = p.value;
    rows.set(p.submissionId, row);
  }
  return Array.from(rows.values()).sort((a, b) =>
    a.occurredAt < b.occurredAt ? 1 : a.occurredAt > b.occurredAt ? -1 : 0,
  );
};
